import React, { useState, useEffect, useRef } from "react";
import { updateSkillsSection, updateSkillsSectionTemplate3, updateListSection, parseDoc, getDocHtml, formatSectionTitle } from "./ResumeEditorHelpers";

function preProcessSkills(skillsArray) {
  return skillsArray.map(skill => {
    // Convert the proficiency to a number (assuming it's between 0 and 5)
    const proficiency = parseInt(skill.proficiency, 10) || 0;
    // Create an array of 5 booleans:
    // For each index, if it's less than the proficiency, mark it as true (filled)
    const filled = Array.from({ length: 5 }, (_, i) => i < proficiency);
    // Return a new skill object with the 'filled' property added.
    return { ...skill, filled };
  });
}

export function SkillsEditor({ 
  resumeHtml, 
  resumeData, // New prop to provide fallback skills data
  editedData, 
  setEditedData, 
  setResumeHtml, 
  templateId,
  saveToHistory 
}) {
  // For templates 3 & 4, we use a rating system.
  const isSkillRatingTemplate = (templateId === 3 || templateId === 4);

  // Local state for skills.
  // For rating templates: each element is { name, proficiency }.
  // For non-rating templates: each element is a string.
  const [skills, setSkills] = useState([]);
  const initialUpdateDone = useRef(false);

  useEffect(() => {
    const rawSkills = editedData.skills || (resumeData && resumeData.skills) || [];
    
    if (templateId === 3) {
      // Only preprocess for template 3.
      const processed = preProcessSkills(rawSkills.map(item => {
        if (typeof item === "string") {
          return { name: item, proficiency: "1" };
        }
        return item;
      }));
      setSkills(processed);
    } else if (templateId === 4) {
      // For template 4, assume each skill is an object with name and proficiency.
      const mapped = rawSkills.map(item => {
        if (typeof item === "object" && item !== null) {
          return {
            name: item.name || "",
            proficiency: item.proficiency || "1"
          };
        }
        return { name: item, proficiency: "1" };
      });
      setSkills(mapped);
    } else {
      // Other templates.
      const mapped = rawSkills.map(item =>
        typeof item === "string" ? item : (item.name || "")
      );
      setSkills(mapped);
    }
  }, [editedData, resumeData, templateId]);
  

    // Automatically update resumeHtml on initial load only.
    useEffect(() => {
      if (!initialUpdateDone.current && skills.length > 0) {
        // Call your update function once.
        handleUpdateSkills();
        initialUpdateDone.current = true;
      }
    }, [skills]);

  // Handle changes for skill name or proficiency.
  const handleSkillChange = (index, field, value) => {
    setSkills(prev => {
      const newSkills = [...prev];
      if (isSkillRatingTemplate) {
        newSkills[index] = {
          ...newSkills[index],
          [field]: value
        };
      } else {
        newSkills[index] = value;
      }
      return newSkills;
    });
  };

  // Add a new skill with default proficiency "1"
  const addSkill = () => {
    if (isSkillRatingTemplate) {
      setSkills(prev => [...prev, { name: "", proficiency: "1" }]);
    } else {
      setSkills(prev => [...prev, ""]);
    }
  };

  // Remove a skill.
  const removeSkill = (index) => {
    setSkills(prev => prev.filter((_, i) => i !== index));
  };

  // Update resume HTML when skills are updated.
  const handleUpdateSkills = () => {
    saveToHistory();

    let updatedHtml;
    if (templateId === 3) {
      updatedHtml = updateSkillsSectionTemplate3(resumeHtml, skills);
    } else if (templateId === 4) {
      updatedHtml = updateSkillsSection(resumeHtml, skills); 
    } else {
      // Non-rating templates or older approach
      updatedHtml = updateListSection(resumeHtml, "skills", skills, "skills-list");
    }    

    setResumeHtml(updatedHtml);
    setEditedData(prev => ({ ...prev, skills }));
  };

  return (
    <div>
      <h3>Edit Skills</h3>
      {skills.map((skill, index) => (
        <div key={index} style={{ marginBottom: "8px" }}>
          {isSkillRatingTemplate ? (
            <>
              <input
                type="text"
                value={skill.name}
                onChange={(e) => handleSkillChange(index, "name", e.target.value)}
                placeholder="Skill Name"
                style={{ marginRight: "8px" }}
              />
              <select
                value={skill.proficiency}
                onChange={(e) => handleSkillChange(index, "proficiency", e.target.value)}
                style={{ marginRight: "8px" }}
              >
                <option value="0">Select (0%)</option>
                <option value="1">1 (Low)</option>
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="4">4</option>
                <option value="5">5 (Expert)</option>
              </select>
              <button onClick={() => removeSkill(index)}>Remove</button>
            </>
          ) : (
            <>
              <input
                type="text"
                value={skill}
                onChange={(e) => handleSkillChange(index, "name", e.target.value)}
                placeholder="Skill"
                style={{ marginRight: "8px" }}
              />
              <button onClick={() => removeSkill(index)}>Remove</button>
            </>
          )}
        </div>
      ))}
      <button onClick={addSkill} style={{ marginRight: "8px" }}>+ Add Skill</button>
      <button onClick={handleUpdateSkills}>Update Skills</button>
    </div>
  );
}